import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';

import { DonateContent } from '../../components/DonateContent';
import ResultSMap from '../../components/ResultSMap';
import { TOTAL_ROUNDS_MAX, resultPathColors } from '../../constants/game';
import useGetRandomUserToken from '../../hooks/useGetRandomUserToken';
import { roundToTwoDecimal, sortPlayersByHighestScore } from '../../util';
import {
    PageContainer,
    ContentWrapper,
    HeroSection,
    Title,
    Subtitle,
    ScoreHero,
    ScoreCircle,
    ScoreRing,
    ScoreRingBackground,
    ScoreRingProgress,
    ScoreValue,
    ScoreNumber,
    ScoreLabel,
    AccuracyBadge,
    AccuracyIcon,
    AccuracyText,
    SectionTitle,
    PodiumContainer,
    PodiumPlace,
    PodiumAvatar,
    PodiumName,
    PodiumScore,
    PodiumBlock,
    PlayersList,
    PlayerCard,
    PlayerRank,
    PlayerInfo,
    PlayerName,
    PlayerSubtext,
    PlayerScore,
    RoundsGrid,
    RoundCard,
    RoundIndicator,
    RoundInfo,
    RoundTitle,
    RoundLocation,
    RoundDistance,
    RoundScore,
    ActionsSection,
    PrimaryButton,
    MapSection,
    MapHeader,
    MapIcon,
    MapTitle,
    DonateWrapper,
    ConfettiPiece,
} from './ResultStyles';

const PODIUM_EMOJIS = ['🥇', '🥈', '🥉'];
const AVATAR_EMOJIS = ['👑', '🎯', '🎮', '🎲', '🌟', '🚀', '💎', '🔥'];

export const BattleResult = () => {
    const randomUserToken = useGetRandomUserToken();
    const lastResult = useSelector(state => state.result);

    const [activePlayerIndex, setActivePlayerIndex] = useState(null);

    const { mode, city, radius, players } = lastResult;

    const finalPlayerOrderStructure = useMemo(() => {
        if (!players) return [];

        const playersResults = [];
        for (let i = 0; i < players.length; i++) {
            const { name, userId } = players[i];
            let score = 0;
            const playerGuessedPoints = [];
            for (let j = 0; j < TOTAL_ROUNDS_MAX; j++) {
                const roundScore = players[i][`round${j + 1}`];
                score += roundScore?.score ? Math.round(roundScore.score) : 0;
                playerGuessedPoints.push(roundScore ?? null);
            }
            playersResults.push({
                name,
                odId: userId,
                score: Math.round(score),
                guessedPoints: playerGuessedPoints,
            });
        }
        return sortPlayersByHighestScore(playersResults);
    }, [players]);

    // Find my position and set as default active
    const myPlayerIndex = useMemo(() => {
        for (let i = 0; i < finalPlayerOrderStructure.length; i++) {
            if (finalPlayerOrderStructure[i].odId === randomUserToken) {
                return i;
            }
        }
        return 0;
    }, [finalPlayerOrderStructure, randomUserToken]);

    // Set initial active player
    const currentActiveIndex = activePlayerIndex ?? myPlayerIndex;
    const activePlayer = finalPlayerOrderStructure[currentActiveIndex];

    const myPlayer = finalPlayerOrderStructure[myPlayerIndex];
    const myAccuracy = myPlayer ? Math.round(myPlayer.score / TOTAL_ROUNDS_MAX) : 0;
    const myRank = myPlayerIndex + 1;

    const getPlacementEmoji = rank => {
        if (rank === 1) return '🏆';
        if (rank === 2) return '🥈';
        if (rank === 3) return '🥉';
        return '🎮';
    };

    const getPlacementText = (rank, total) => {
        if (rank === 1) return 'Vítěz!';
        if (rank === 2) return 'Skvělé druhé místo!';
        if (rank === 3) return 'Bronzová medaile!';
        if (rank <= Math.ceil(total / 2)) return 'Dobrý výkon!';
        return 'Příště to bude lepší';
    };

    const showConfetti = myRank === 1;

    // Get podium players (top 3)
    const podiumPlayers = finalPlayerOrderStructure.slice(0, 3);
    const hasEnoughForPodium = podiumPlayers.length >= 2;

    // Get remaining players (4th place onwards)
    const remainingPlayers = finalPlayerOrderStructure.slice(3);

    if (players && finalPlayerOrderStructure.length > 0) {
        return (
            <PageContainer>
                {/* Confetti for winner */}
                {showConfetti && (
                    <>
                        {[...Array(25)].map((_, i) => (
                            <ConfettiPiece
                                key={i}
                                $delay={Math.random() * 2}
                                $x={Math.random() * 100}
                                $color={resultPathColors[i % resultPathColors.length]}
                            />
                        ))}
                    </>
                )}

                <ContentWrapper>
                    <HeroSection>
                        <Subtitle>Hra dokončena</Subtitle>
                        <Title>Výsledky bitvy</Title>
                    </HeroSection>

                    {/* My score circle */}
                    <ScoreHero>
                        <ScoreCircle>
                            <ScoreRing viewBox="0 0 100 100">
                                <ScoreRingBackground cx="50" cy="50" r="45" />
                                <ScoreRingProgress
                                    cx="50"
                                    cy="50"
                                    r="45"
                                    $percent={myAccuracy}
                                    $color={
                                        myRank === 1 ? '#fbbf24' :
                                        myRank === 2 ? '#94a3b8' :
                                        myRank === 3 ? '#cd7c2f' :
                                        myAccuracy >= 70 ? '#22c55e' :
                                        myAccuracy >= 40 ? '#fbbf24' :
                                        '#c80707'
                                    }
                                />
                            </ScoreRing>
                            <ScoreValue>
                                <ScoreNumber>{myPlayer?.score || 0}</ScoreNumber>
                                <ScoreLabel>bodů</ScoreLabel>
                            </ScoreValue>
                        </ScoreCircle>

                        <AccuracyBadge $percent={myRank <= 3 ? 70 : myAccuracy}>
                            <AccuracyIcon>{getPlacementEmoji(myRank)}</AccuracyIcon>
                            <AccuracyText>
                                {myRank}. místo – {getPlacementText(myRank, finalPlayerOrderStructure.length)}
                            </AccuracyText>
                        </AccuracyBadge>
                    </ScoreHero>

                    {/* Podium for top 3 */}
                    {hasEnoughForPodium && (
                        <>
                            <SectionTitle>Stupně vítězů</SectionTitle>
                            <PodiumContainer>
                                {/* Second place */}
                                {podiumPlayers[1] && (
                                    <PodiumPlace $place={2} $isMe={podiumPlayers[1].odId === randomUserToken}>
                                        <PodiumAvatar $place={2} $isMe={podiumPlayers[1].odId === randomUserToken}>
                                            {AVATAR_EMOJIS[1]}
                                        </PodiumAvatar>
                                        <PodiumName $isMe={podiumPlayers[1].odId === randomUserToken}>
                                            {podiumPlayers[1].name}
                                        </PodiumName>
                                        <PodiumScore>{podiumPlayers[1].score} bodů</PodiumScore>
                                        <PodiumBlock $place={2}>2</PodiumBlock>
                                    </PodiumPlace>
                                )}

                                {/* First place */}
                                {podiumPlayers[0] && (
                                    <PodiumPlace $place={1} $isMe={podiumPlayers[0].odId === randomUserToken}>
                                        <PodiumAvatar $place={1} $isMe={podiumPlayers[0].odId === randomUserToken}>
                                            {AVATAR_EMOJIS[0]}
                                        </PodiumAvatar>
                                        <PodiumName $isMe={podiumPlayers[0].odId === randomUserToken}>
                                            {podiumPlayers[0].name}
                                        </PodiumName>
                                        <PodiumScore>{podiumPlayers[0].score} bodů</PodiumScore>
                                        <PodiumBlock $place={1}>1</PodiumBlock>
                                    </PodiumPlace>
                                )}

                                {/* Third place */}
                                {podiumPlayers[2] && (
                                    <PodiumPlace $place={3} $isMe={podiumPlayers[2].odId === randomUserToken}>
                                        <PodiumAvatar $place={3} $isMe={podiumPlayers[2].odId === randomUserToken}>
                                            {AVATAR_EMOJIS[2]}
                                        </PodiumAvatar>
                                        <PodiumName $isMe={podiumPlayers[2].odId === randomUserToken}>
                                            {podiumPlayers[2].name}
                                        </PodiumName>
                                        <PodiumScore>{podiumPlayers[2].score} bodů</PodiumScore>
                                        <PodiumBlock $place={3}>3</PodiumBlock>
                                    </PodiumPlace>
                                )}
                            </PodiumContainer>
                        </>
                    )}

                    {/* Remaining players list */}
                    {remainingPlayers.length > 0 && (
                        <>
                            <SectionTitle>Další hráči</SectionTitle>
                            <PlayersList>
                                {remainingPlayers.map((player, i) => {
                                    const rank = i + 4;
                                    const isMe = player.odId === randomUserToken;
                                    const isActive = currentActiveIndex === i + 3;

                                    return (
                                        <PlayerCard
                                            key={player.odId}
                                            $isMe={isMe}
                                            $isActive={isActive}
                                            $rank={rank}
                                            onClick={() => setActivePlayerIndex(i + 3)}
                                        >
                                            <PlayerRank $rank={rank}>{rank}</PlayerRank>
                                            <PlayerInfo>
                                                <PlayerName $isMe={isMe}>
                                                    {player.name} {isMe && '(ty)'}
                                                </PlayerName>
                                                <PlayerSubtext>
                                                    {Math.round(player.score / TOTAL_ROUNDS_MAX)}% přesnost
                                                </PlayerSubtext>
                                            </PlayerInfo>
                                            <PlayerScore>{player.score} bodů</PlayerScore>
                                        </PlayerCard>
                                    );
                                })}
                            </PlayersList>
                        </>
                    )}

                    {/* Full leaderboard clickable */}
                    <SectionTitle>Klikni pro zobrazení kol</SectionTitle>
                    <PlayersList>
                        {finalPlayerOrderStructure.map((player, i) => {
                            const rank = i + 1;
                            const isMe = player.odId === randomUserToken;
                            const isActive = currentActiveIndex === i;

                            return (
                                <PlayerCard
                                    key={`full-${player.odId}`}
                                    $isMe={isMe}
                                    $isActive={isActive}
                                    $rank={rank}
                                    onClick={() => setActivePlayerIndex(i)}
                                >
                                    <PlayerRank $rank={rank}>
                                        {rank <= 3 ? PODIUM_EMOJIS[rank - 1] : rank}
                                    </PlayerRank>
                                    <PlayerInfo>
                                        <PlayerName $isMe={isMe}>
                                            {player.name} {isMe && '(ty)'}
                                        </PlayerName>
                                        <PlayerSubtext>
                                            {Math.round(player.score / TOTAL_ROUNDS_MAX)}% přesnost
                                        </PlayerSubtext>
                                    </PlayerInfo>
                                    <PlayerScore>{player.score} bodů</PlayerScore>
                                </PlayerCard>
                            );
                        })}
                    </PlayersList>

                    {/* Rounds detail for selected player */}
                    {activePlayer && (
                        <>
                            <SectionTitle>
                                Kola – {activePlayer.name}
                                {activePlayer.odId === randomUserToken && ' (ty)'}
                            </SectionTitle>
                            <RoundsGrid>
                                {activePlayer.guessedPoints.map((round, i) => {
                                    const color = resultPathColors[i] || '#6e45f9';
                                    const hasGuessed = round !== null;
                                    const score = hasGuessed ? Math.round(round.score) : 0;
                                    const distance = hasGuessed ? round.distance : -1;
                                    const cityInfo = round?.currentCity;
                                    const locationText = cityInfo
                                        ? `${cityInfo.obec}, ${cityInfo.okres}`
                                        : null;

                                    return (
                                        <RoundCard key={i} $index={i} $color={color}>
                                            <RoundIndicator $color={color}>
                                                {i + 1}
                                            </RoundIndicator>
                                            <RoundInfo>
                                                <RoundTitle>Kolo {i + 1}</RoundTitle>
                                                {locationText && (
                                                    <RoundLocation>{locationText}</RoundLocation>
                                                )}
                                            </RoundInfo>
                                            <RoundDistance $missed={!hasGuessed}>
                                                {hasGuessed && distance >= 0
                                                    ? `${roundToTwoDecimal(distance)} km`
                                                    : 'Netipováno'}
                                            </RoundDistance>
                                            <RoundScore $score={score}>
                                                {score} bodů
                                            </RoundScore>
                                        </RoundCard>
                                    );
                                })}
                            </RoundsGrid>
                        </>
                    )}

                    <ActionsSection>
                        <PrimaryButton to="/">
                            <span>🎮</span>
                            Hrát znovu
                        </PrimaryButton>
                    </ActionsSection>

                    <DonateWrapper>
                        <DonateContent marginBottom={0} withDonate />
                    </DonateWrapper>

                    {activePlayer && (
                        <MapSection>
                            <MapHeader>
                                <MapIcon>🗺️</MapIcon>
                                <MapTitle>
                                    Mapa tipů – {activePlayer.name}
                                    {activePlayer.odId === randomUserToken && ' (ty)'}
                                </MapTitle>
                            </MapHeader>
                            <ResultSMap
                                guessedPoints={activePlayer.guessedPoints}
                                mode={mode}
                                city={city}
                                radius={radius}
                            />
                        </MapSection>
                    )}
                </ContentWrapper>
            </PageContainer>
        );
    }

    return <Redirect to={{ pathname: '/' }} />;
};
